import React, { useEffect, useMemo, useState } from "react";
import { Plus, X, Check, Ban, Trash2, AlertCircle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  EXPENSE_CATEGORIES,
  TREASURY_ACCOUNTS,
  EXPENSE_STATUS,
  APPROVAL_THRESHOLD,
  categoryLabel,
  accountLabel,
} from "./financeConstants";
import { PromptModal, ConfirmModal } from "@/components/AppDialogs";
import { toast } from "sonner";

/**
 * Expense ledger.
 *
 * - All Finance users can record expenses; the server auto-routes them to
 *   `pending_approval` if amount > $1000 AND category != supplier_purchase.
 * - Approve / Reject buttons are visible only when `canApprove` (super_admin).
 */
export default function ExpensesPanel({ canApprove, fmt }) {
  const [rows, setRows] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: "", category: "", account: "" });
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [rejectFor, setRejectFor] = useState(null); // expense
  const [deleteFor, setDeleteFor] = useState(null); // expense

  const refresh = () => {
    setLoading(true);
    const q = new URLSearchParams();
    Object.entries(filter).forEach(([k, v]) => { if (v) q.set(k, v); });
    api.get(`/admin/expenses?${q.toString()}`)
      .then((r) => setRows(r.data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
    api.get("/admin/suppliers")
      .then((r) => setSuppliers(r.data))
      .catch(() => setSuppliers([]));
  };

  useEffect(() => { refresh(); }, [filter.status, filter.category, filter.account]);

  const totals = useMemo(() => {
    const t = { approved: 0, pending: 0, rejected: 0 };
    rows.forEach((r) => {
      if (r.status === "approved") t.approved += r.amount;
      else if (r.status === "pending_approval") t.pending += r.amount;
      else if (r.status === "rejected") t.rejected += r.amount;
    });
    return t;
  }, [rows]);

  const approve = async (exp) => {
    setBusyId(exp.id);
    try {
      await api.post(`/admin/expenses/${exp.id}/approve`);
      refresh();
      toast.success("Dépense approuvée.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    } finally { setBusyId(null); }
  };

  const reject = (exp) => setRejectFor(exp);
  const submitReject = async (reason) => {
    const exp = rejectFor;
    setRejectFor(null);
    if (!exp) return;
    setBusyId(exp.id);
    try {
      await api.post(`/admin/expenses/${exp.id}/reject`, { reason });
      refresh();
      toast.success("Dépense rejetée.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    } finally { setBusyId(null); }
  };

  const remove = (exp) => setDeleteFor(exp);
  const performRemove = async () => {
    const exp = deleteFor;
    setDeleteFor(null);
    if (!exp) return;
    try {
      await api.delete(`/admin/expenses/${exp.id}`);
      refresh();
      toast.success("Dépense supprimée.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  return (
    <div data-testid="expenses-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <h2 className="font-display text-xl font-black text-[#2A0E3C]">Dépenses</h2>
          <p className="text-xs text-[#5C4664] mt-0.5">
            Montant / Porteur / Motif — catégorisé pour les rapports. Validation super_admin
            au-delà de <strong>${APPROVAL_THRESHOLD}</strong> hors achats fournisseurs.
          </p>
        </div>
        <Button
          onClick={() => setCreating(true)}
          data-testid="expense-new-btn"
          className="rounded-full bg-[#D81B8C] hover:bg-[#A60F6E] text-white gap-2"
        >
          <Plus className="w-4 h-4" /> Nouvelle dépense
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <KpiSmall label="Approuvées" value={fmt(totals.approved)} color="#3FA535" testid="exp-kpi-approved" />
        <KpiSmall label="En attente" value={fmt(totals.pending)} color="#FF8A00" testid="exp-kpi-pending" />
        <KpiSmall label="Rejetées" value={fmt(totals.rejected)} color="#B91C1C" testid="exp-kpi-rejected" />
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap mb-4">
        <FilterSelect
          testid="exp-filter-status"
          value={filter.status}
          onChange={(v) => setFilter({ ...filter, status: v })}
          options={[
            { v: "", l: "Tous statuts" },
            { v: "approved", l: "Approuvées" },
            { v: "pending_approval", l: "En attente" },
            { v: "rejected", l: "Rejetées" },
          ]}
        />
        <FilterSelect
          testid="exp-filter-category"
          value={filter.category}
          onChange={(v) => setFilter({ ...filter, category: v })}
          options={[
            { v: "", l: "Toutes catégories" },
            ...EXPENSE_CATEGORIES.map((c) => ({ v: c.id, l: c.label })),
          ]}
        />
        <FilterSelect
          testid="exp-filter-account"
          value={filter.account}
          onChange={(v) => setFilter({ ...filter, account: v })}
          options={[
            { v: "", l: "Tous comptes" },
            ...TREASURY_ACCOUNTS.map((a) => ({ v: a.id, l: a.label })),
          ]}
        />
      </div>

      {loading ? (
        <p className="text-sm text-[#5C4664]">Chargement…</p>
      ) : rows.length === 0 ? (
        <div data-testid="expenses-empty" className="bg-white border border-dashed border-[#F2E2EC] rounded-2xl p-8 text-center text-sm text-[#5C4664]">
          Aucune dépense enregistrée pour ces filtres.
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-[#F2E2EC] overflow-hidden">
          <table className="w-full text-sm" data-testid="expenses-table">
            <thead className="bg-[#FCE7F3] text-[10px] uppercase tracking-widest text-[#5C4664]">
              <tr>
                <th className="text-left px-3 py-2.5">Date</th>
                <th className="text-left px-3 py-2.5">Motif</th>
                <th className="text-left px-3 py-2.5">Catégorie</th>
                <th className="text-left px-3 py-2.5">Porteur</th>
                <th className="text-left px-3 py-2.5">Compte</th>
                <th className="text-right px-3 py-2.5">Montant</th>
                <th className="text-left px-3 py-2.5">Statut</th>
                <th className="text-right px-3 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((exp) => (
                <tr
                  key={exp.id}
                  data-testid={`expense-row-${exp.id}`}
                  className="border-t border-[#F2E2EC] hover:bg-[#FFF9FB]"
                >
                  <td className="px-3 py-2.5 text-xs text-[#5C4664] whitespace-nowrap">
                    {exp.paid_at ? new Date(exp.paid_at).toLocaleDateString("fr-FR") : "—"}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="font-semibold text-[#2A0E3C] leading-tight">{exp.motif}</div>
                    {exp.supplier_name && (
                      <div className="text-[10px] uppercase tracking-widest text-[#A60F6E] mt-0.5">
                        Fournisseur : {exp.supplier_name}
                      </div>
                    )}
                    {exp.event_title && (
                      <div className="text-[10px] text-[#5C4664] mt-0.5">
                        Événement : {exp.event_title}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-xs">{categoryLabel(exp.category)}</td>
                  <td className="px-3 py-2.5 text-xs">{exp.porteur}</td>
                  <td className="px-3 py-2.5 text-xs">{accountLabel(exp.account)}</td>
                  <td className="px-3 py-2.5 text-right font-display font-black text-[#D81B8C]">
                    {fmt(exp.amount)}
                  </td>
                  <td className="px-3 py-2.5">
                    <StatusBadge status={exp.status} />
                    {exp.rejection_reason && (
                      <div className="text-[10px] text-red-700 mt-0.5 max-w-[160px]">{exp.rejection_reason}</div>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <div className="inline-flex gap-1">
                      {canApprove && exp.status === "pending_approval" && (
                        <>
                          <button
                            onClick={() => approve(exp)}
                            disabled={busyId === exp.id}
                            data-testid={`expense-approve-${exp.id}`}
                            title="Approuver"
                            className="w-7 h-7 rounded-full bg-[#3FA535] hover:bg-[#2E7E27] text-white flex items-center justify-center disabled:opacity-50"
                          >
                            <Check className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => reject(exp)}
                            disabled={busyId === exp.id}
                            data-testid={`expense-reject-${exp.id}`}
                            title="Rejeter"
                            className="w-7 h-7 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center disabled:opacity-50"
                          >
                            <Ban className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                      {canApprove && (
                        <button
                          onClick={() => remove(exp)}
                          data-testid={`expense-delete-${exp.id}`}
                          title="Supprimer (super_admin)"
                          className="w-7 h-7 rounded-full border border-[#F2E2EC] hover:bg-red-50 hover:border-red-300 text-[#5C4664] hover:text-red-600 flex items-center justify-center"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <ExpenseFormModal
          suppliers={suppliers}
          onCancel={() => setCreating(false)}
          onSubmit={async (form) => {
            try {
              await api.post("/admin/expenses", form);
              setCreating(false);
              refresh();
            } catch (err) {
              throw new Error(formatApiError(err.response?.data?.detail) || "Erreur");
            }
          }}
        />
      )}
      <PromptModal
        open={!!rejectFor}
        onClose={() => setRejectFor(null)}
        onSubmit={submitReject}
        title={rejectFor ? `Rejeter « ${rejectFor.motif} »` : ""}
        description={rejectFor ? `Montant : $${rejectFor.amount}. Indiquez la raison du rejet (audit).` : ""}
        placeholder="Ex: motif non conforme"
        kind="textarea"
        submitLabel="Rejeter la dépense"
        testId="expense-reject-modal"
      />
      <ConfirmModal
        open={!!deleteFor}
        onClose={() => setDeleteFor(null)}
        onConfirm={performRemove}
        title="Supprimer cette dépense ?"
        description={deleteFor ? `« ${deleteFor.motif} » — $${deleteFor.amount}. Action irréversible.` : ""}
        confirmLabel="Supprimer"
        destructive
        testId="expense-delete-confirm"
      />
    </div>
  );
}

function StatusBadge({ status }) {
  const cfg = EXPENSE_STATUS[status] || { label: status, color: "#5C4664", bg: "#F2E2EC" };
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest"
      style={{ color: cfg.color, backgroundColor: cfg.bg }}
    >
      {cfg.label}
    </span>
  );
}

function KpiSmall({ label, value, color, testid }) {
  return (
    <div data-testid={testid} className="bg-white rounded-2xl border border-[#F2E2EC] p-3">
      <p className="text-[10px] font-bold uppercase tracking-widest text-[#5C4664]">{label}</p>
      <p className="font-display text-xl font-black mt-0.5" style={{ color }}>{value}</p>
    </div>
  );
}

function FilterSelect({ value, onChange, options, testid }) {
  return (
    <select
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-full border border-[#F2E2EC] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#D81B8C]"
    >
      {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
  );
}

function ExpenseFormModal({ suppliers, onCancel, onSubmit }) {
  const [form, setForm] = useState({
    amount: "",
    category: "supplier_purchase",
    motif: "",
    porteur: "",
    account: "cash_bureau",
    supplier_id: "",
    event_id: "",
    paid_at: new Date().toISOString().slice(0, 10),
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const needsSupplier = form.category === "supplier_purchase";
  const amountNum = parseFloat(form.amount) || 0;
  const needsApproval = amountNum > APPROVAL_THRESHOLD && form.category !== "supplier_purchase";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const payload = {
      amount: amountNum,
      category: form.category,
      motif: form.motif.trim(),
      porteur: form.porteur.trim(),
      account: form.account,
      supplier_id: form.supplier_id || null,
      event_id: form.event_id || null,
      paid_at: form.paid_at ? new Date(form.paid_at).toISOString() : null,
    };
    try {
      await onSubmit(payload);
    } catch (err) {
      setError(err.message || "Erreur");
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="expense-form-modal" className="fixed inset-0 z-50 bg-[#2A0E3C]/70 backdrop-blur-sm flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white rounded-3xl w-full max-w-lg p-6 space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between">
          <h3 className="font-display text-xl font-black text-[#2A0E3C]">Nouvelle dépense</h3>
          <button type="button" onClick={onCancel} className="text-[#5C4664]" aria-label="Fermer">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Montant *">
            <Input
              data-testid="expense-form-amount"
              type="number"
              min="0.01"
              step="0.01"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              required
              className="rounded-xl"
            />
          </Field>
          <Field label="Date de paiement">
            <Input
              data-testid="expense-form-paid-at"
              type="date"
              value={form.paid_at}
              onChange={(e) => setForm({ ...form, paid_at: e.target.value })}
              className="rounded-xl"
            />
          </Field>
        </div>

        <Field label="Catégorie *">
          <select
            data-testid="expense-form-category"
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value, supplier_id: "" })}
            className="w-full rounded-xl border border-[#F2E2EC] bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#D81B8C]"
          >
            {EXPENSE_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </Field>

        {needsSupplier && (
          <Field label="Fournisseur *">
            <select
              data-testid="expense-form-supplier"
              value={form.supplier_id}
              onChange={(e) => setForm({ ...form, supplier_id: e.target.value })}
              required
              className="w-full rounded-xl border border-[#F2E2EC] bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#D81B8C]"
            >
              <option value="">— Choisir un fournisseur —</option>
              {suppliers.filter((s) => !s.archived).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            {suppliers.length === 0 && (
              <p className="text-xs text-amber-700 mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Aucun fournisseur dans le référentiel. Demandez à un admin d&apos;en créer un.
              </p>
            )}
          </Field>
        )}

        <Field label="Motif *">
          <Input
            data-testid="expense-form-motif"
            value={form.motif}
            onChange={(e) => setForm({ ...form, motif: e.target.value })}
            placeholder="Ex: Mixeurs commande #4521"
            required
            className="rounded-xl"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Porteur *">
            <Input
              data-testid="expense-form-porteur"
              value={form.porteur}
              onChange={(e) => setForm({ ...form, porteur: e.target.value })}
              placeholder="Ex: Logistique, DAF…"
              required
              className="rounded-xl"
            />
          </Field>
          <Field label="Compte payé depuis *">
            <select
              data-testid="expense-form-account"
              value={form.account}
              onChange={(e) => setForm({ ...form, account: e.target.value })}
              className="w-full rounded-xl border border-[#F2E2EC] bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#D81B8C]"
            >
              {TREASURY_ACCOUNTS.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
            </select>
          </Field>
        </div>

        {needsApproval && (
          <div data-testid="expense-form-approval-notice" className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800 flex gap-2">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              Montant supérieur à <strong>${APPROVAL_THRESHOLD}</strong> hors fournisseurs —
              la dépense sera créée en <strong>« en attente »</strong> et devra être approuvée par un super_admin.
            </span>
          </div>
        )}

        {error && <p data-testid="expense-form-error" className="text-sm text-red-600">{error}</p>}

        <div className="flex gap-2 pt-2">
          <Button type="button" onClick={onCancel} variant="outline" className="flex-1 rounded-full border-[#F2E2EC]">
            Annuler
          </Button>
          <Button
            type="submit"
            disabled={submitting}
            data-testid="expense-form-submit"
            className="flex-1 rounded-full bg-[#D81B8C] hover:bg-[#A60F6E] text-white disabled:opacity-50"
          >
            {submitting ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-bold uppercase tracking-widest text-[#5C4664] mb-1.5 block">
        {label}
      </Label>
      {children}
    </div>
  );
}
